import {
  IGetStarted,
  SimpleGetStarted
} from 'apps/user/types/get-started.type';
import {
  IdentityType,
  PersonalInfo,
  UpdatePasswordDto,
  UpdateProfileInfo
} from 'apps/user/types/on-boarding.type';
import {
  IdentificationTypeEnum,
  SpecialInstruction
} from 'apps/user/types/user.enum.type';
import { AxiosError, AxiosInstance } from 'axios';
import { getUnixTime } from 'date-fns';
import { errorHandler } from 'utils';

class ManageUsersService {
  private baseService: AxiosInstance;

  constructor(baseService: AxiosInstance) {
    this.baseService = baseService;
  }

  authenticate = async () => {
    const response = await this.baseService.get(`/users/validate-auth`);
    return response?.data;
  };

  createUser = async (iGetStarted: IGetStarted | SimpleGetStarted) => {
    try {
      const response = await this.baseService.post(
        '/users/create',
        iGetStarted
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  checkIfEmailInUse = async (email: string) => {
    const response = await this.baseService.get(`/users/email-exists/${email}`);
    return response.data;
  };

  updatePersonalInfo = async (personalInfo: PersonalInfo) => {
    try {
      const response = await this.baseService.put(
        '/users/personal-information',
        personalInfo
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  updateIdentityVerificationInfo = async (identityType: IdentityType) => {
    try {
      // The NIN is the National ID number; the backend has no separate NIN type
      const response = await this.baseService.put(
        '/users/identity-verification',
        {
          ...identityType,
          identificationType:
            identityType.identificationType === IdentificationTypeEnum.NIN
              ? IdentificationTypeEnum.NATIONAL_ID
              : identityType.identificationType
        }
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  updateSpecialInstruction = async (specialInstruction: SpecialInstruction) => {
    try {
      const response = await this.baseService.patch(
        '/users/special-instruction',
        specialInstruction
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  updateProfileInfo = async (updateProfileInfo: UpdateProfileInfo) => {
    try {
      const response = await this.baseService.patch(
        '/users/update-personal-information',
        updateProfileInfo
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  updateProfilePassword = async (updatePasswordDto: UpdatePasswordDto) => {
    try {
      const response = await this.baseService.patch(
        '/users/update-password',
        updatePasswordDto
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  updateStep = async (currentStep: number) => {
    try {
      const response = await this.baseService.put(
        `/users/update-step/${currentStep}`
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  sendWillForApproval = async () => {
    try {
      const response = await this.baseService.put(
        `/users/send-will-for-approval`
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  generatePDF = async () => {
    try {
      const response = await this.baseService.get(`/users/generate-pdf`, {
        timeout: 40000
      });
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  downloadWill = async ({ user }: any) => {
    try {
      const response = await this.baseService.get(`/users/download-will`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${user.surname}_${user.othername}_${getUnixTime(new Date())}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  skipOnboardingStep = async () => {
    try {
      const response = await this.baseService.put(
        `/users/skip-onboarding-step`
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };
}
export default ManageUsersService;
